import {
  Component,
  Prop,
  Event,
  EventEmitter,
  Method,
  State,
  Element,
  h
} from '@stencil/core';
import { Color, Size } from '../../interface';

@Component({
  tag: 'lar-inner-range-area',
  styleUrl: 'inner-range-area.scss',
  shadow: true
})
export class InnerRangeArea {
  @Element() el!: HTMLLarInnerRangeAreaElement;

  /////// LarvaNode base properties
  /**
   * Component main icon
   */
  @Prop({ mutable: true }) icon?: string = 'security';
  /**
   * The color to use from your application's color palette.
   * Detrouble options are: `"primary"`, `"secondary"`, `"tertiary"`, `"success"`, `"warning"`, `"danger"`, `"light"`, `"medium"`, and `"dark"`.
   */
  @Prop({ mutable: true }) color?: Color = 'primary';
  /**
   * The color to use from your application's color palette for Components modal window.
   */
  @Prop({ mutable: true }) colorModal?: Color;
  /**
   * The color to use from your application's color palette for inputs
   */
  @Prop({ mutable: true }) colorInputs?: Color;
  /**
   * The color to use from your application's color palette for indication icon
   */
  @Prop() colorIconSmall?: Color;
  /**
   * Allow node indication color automatic change based on feedback/node value. Defaults to false
   */
  @Prop() allowIndicationAutoColoring: boolean = false;
  /**
   * Allow node color automatic change based on feedback/node value. Defaults to true
   */
  @Prop() allowNodeAutoColoring: boolean = true;
  /**
   * Hide node titles
   */
  @Prop() hideTitles?: boolean;
  /**
   * Component superscript title
   */
  @Prop() supTitle?: string;
  /**
   * Component subtitle
   */
  @Prop() subTitle?: string;
  /**
   * Is logging for this component enabled (lar-log subcomponent loaded)
   */
  @Prop() log: boolean = false;
  /**
   * Component main title
   */
  @Prop() mainTitle!: string;
  /**
   * Node size
   */
  @Prop() nodeSize?: Size = 'default';
  /**
   * @see {@link ../readme.md} chapter "Components input and output" for further information.
   */
  @Event() output!: EventEmitter;
  /**
   * @see {@link ../readme.md} chapter "Sub-Components requests and responses" for further information.
   */
  @Event() request!: EventEmitter;
  /////// LarvaNode base properties and events - end

  @State() armed: boolean = false;
  @State() alarm: boolean = false;
  @State() loading: boolean = true;
  private node!: HTMLLarNodeElement;

  /**
   * Larva error input
   */
  @Method()
  async error(data: any) {
    if (this.node) {
      this.node.error(data);
    }
    this.loading = false;
  }
  /**
   * Larva input message
   */
  @Method()
  async input(data: any) {
    if (data && data.State) {
      this.armed = data.State === 'Armed';
      this.loading = false;
    }
  }

  componentDidLoad() {
    const el = this.el.shadowRoot || this.el;
    this.node = el.querySelector('lar-node') as HTMLLarNodeElement;
    this.loading = true;
    this.output.emit();
  }

  armDisarm() {
    if (this.loading === false) {
      this.loading = true;
      return this.output.emit({ command: this.armed ? 'disarm' : 'arm' });
    }
  }

  render() {
    const iconSmall = this.armed ? 'lock' : 'unlock';
    const componentProps = {
      onClick: () => this.armDisarm(),
      value: this.armed ? 'area.arm' : 'area.disarm',
      icon: iconSmall,
      disabled: this.loading,
      color: this.colorInputs || this.color
    };
    return [
      <lar-node
        value={this.armed ? 'area.armed' : 'area.disarmed'}
        hideTitles={this.hideTitles}
        iconSmall={iconSmall}
        icon={this.icon}
        colorIconSmall={this.alarm && this.allowIndicationAutoColoring === true ? 'danger' : this.colorIconSmall}
        color={this.alarm && this.allowNodeAutoColoring === true ? 'danger' : this.color}
        supTitle={this.supTitle}
        mainTitle={this.mainTitle}
        subTitle={this.subTitle}
        colorModal={this.alarm && this.allowNodeAutoColoring === true ? 'danger' : this.colorModal}
        colorInputs={this.colorInputs}
        log={this.log}
        loading={this.loading}
        nodeSize={this.nodeSize}
        component="lar-inner-range-area-door-content"
        componentProps={componentProps}
        >
        <slot />
      </lar-node>
    ];
  }
}
